import React, { FC } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { OPTabInReducer, ScaleFunction } from '@components/operatorPanel/interfaces';
import { fullHeight, scale as initialScale } from '@components/frame';
import { IMAGE_HANDLING_HEADER_HEIGHT } from '@components/imageHandling/common';
import ColorWrapper, { WithColor } from '@styles/colors';
import { font as initialFont } from '@styles/fonts';
import { connect } from 'react-redux';
import { useRoute } from '@react-navigation/native';
import ImagesDocumentsGallery from '@v2/pages/nc-file/details/modules/nc-file/components/displayModes/ImagesDocumentsGallery';
import i18n from 'i18n-js';
import Svg from '@styles/icons/svg';
import { ImageUpload, isWeb } from '@helpers';
import { BOTTOM_SHEET_TOP_OFFSET, BOTTOM_SHEET_TOP_OFFSET_WEB } from '@v2/pages/common/constants/base.constants';
import TouchableElement from '@components/common/touchableElement';
import { incrementTabCount } from '@reducers/operatorPanel';
import { OPERATOR_PANEL_INFORMATION_SCREENS } from '@components/operatorPanel/constants/operatorPanel.constants';
import AttachEntitiesListOP from '@components/operatorPanel/components/elements/AttachEntitiesListOP';
import BackButton from '@components/common/backButton';
import { ImageContextForAdding, ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types.d';
import ImageHandlingHeader, { ImageHandlingHeaderProps } from '@v2/components/imageHandling/components/Header/Header';
import { useManageOPInformationDocuments } from './hooks/useManageOPInformationDocuments';
import { mapDocumentTypeSvg } from '@v2/components/imageHandling/constants';
import { HeaderIconKey } from '@v2/components/imageHandling/components/Header/components/HeaderIcons/types';

export interface OperatorPanelInformationDocumentsProps extends WithColor {
  title: string;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  images: ImageDocumentStoreItem[];
  tabs: OPTabInReducer[];
  imageContextForAdding: ImageContextForAdding;
  shouldHideDownloadButton?: boolean;
  shouldHideAttachButton?: boolean;
  shouldShowAddButtonIfSingleImage?: boolean;
  shouldDisplayModeIcon?: boolean;
  shouldHideThumbnailButton?: boolean;
  withAttachToBlock?: boolean;
  withBackButton?: boolean;
  onBackButtonPress?: () => void;
  noResult: boolean;
  testID?: string;
}

interface NoResultProps extends Pick<OperatorPanelInformationDocumentsProps, 'C' | 'scale' | 'font' | 'tabs' | 'imageContextForAdding'> {
  onSuccessImageUpload: () => void;
}

export const NoResult: FC<NoResultProps> = ({ C, scale = initialScale, font = initialFont, tabs, imageContextForAdding, onSuccessImageUpload }) => {
  const route = useRoute();
  const activeTabConfig = tabs.find((t) => t.value === route.name);

  const screenName = activeTabConfig?.value;

  const icon = activeTabConfig?.noResultIcon ?? activeTabConfig?.icon;
  const docTypeTitle = activeTabConfig?.noResultText ?? activeTabConfig?.title ?? '';

  const isAllDocumentScreen = screenName === OPERATOR_PANEL_INFORMATION_SCREENS.AllDocuments;

  const iconConfig = Object.values(mapDocumentTypeSvg)?.find((c) => c.icon === icon);

  const minWidth = 450;

  const iconColor = C('Label/Light/AlwaysLight Primary');

  const allDocumentsIconConfig = {
    width: scale(14),
    height: scale(18),
    color: iconColor,
  };

  return (
    <View style={{ minWidth: scale(minWidth), alignItems: 'center', gap: scale(18) }}>
      <TouchableElement
        onPress={() =>
          ImageUpload({
            saveToMainStore: true,
            onSuccessImageUpload,
            ...imageContextForAdding,
          })
        }
      >
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              boxStyle,
              {
                backgroundColor: C(pressHoverColor('Button/Light/Primary')),
                borderRadius: scale(36),
                paddingHorizontal: scale(15),
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: scale(22),
                paddingBottom: scale(18),
                gap: scale(10),
                minWidth: scale(minWidth),
              },
            ]}
          >
            <View
              style={{
                backgroundColor: C('Button/Light/Primary Text'),
                width: scale(27),
                height: scale(27),
                borderRadius: scale(27),
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Svg icon="plus" color={C(pressHoverColor('Button/Light/Primary'))} width={scale(14)} height={scale(14)} />
            </View>
            {docTypeTitle && (
              <Text style={[font['Headline Large'], { color: C('Button/Light/Primary Text') }]}>
                {i18n.t(docTypeTitle)}
                {!isAllDocumentScreen ? ` ${i18n.t('Document')}` : ''}
              </Text>
            )}
            {icon && isAllDocumentScreen && <Svg {...allDocumentsIconConfig} icon={icon} />}
            {icon && iconConfig && (
              <Svg icon={icon} width={scale(iconConfig.width * 1.5)} height={scale(iconConfig.height * 1.5)} color={iconColor} />
            )}
          </View>
        )}
      </TouchableElement>
      {docTypeTitle && !isAllDocumentScreen && (
        <Text style={[font.Body, { color: C('Label/Light/Tertiary'), maxWidth: scale(minWidth - 43), textAlign: 'center' }]}>
          {i18n.t(`Mark an existing document as {{docType}} or add a new {{docType}}`, { docType: docTypeTitle })}
        </Text>
      )}
    </View>
  );
};

const OperatorPanelInformationDocuments: FC<OperatorPanelInformationDocumentsProps> = ({
  title,
  C,
  images,
  noResult,
  shouldHideDownloadButton,
  shouldHideAttachButton,
  shouldShowAddButtonIfSingleImage,
  shouldHideThumbnailButton,
  tabs,
  imageContextForAdding,
  shouldDisplayModeIcon,
  withAttachToBlock,
  withBackButton,
  onBackButtonPress,
  scale = initialScale,
  font = initialFont,
  testID,
}) => {
  const {
    selectedIndex,
    setSelectedIndex,
    cropImageDocument: cropImage,
    rotateImageDocument: rotateImage,
    removeImageDocument: deleteImage,
    containerMeasurements,
    setContainerMeasurements,
    attachBlockMeasurements,
    setAttachBlockMeasurements,
    onSuccessImageUpload,
    IMAGE_PADDING_HORIZONTAL,
    onClose,
    selectedImage,
    bg,
    borderColor,
    imageBg,
    setMode,
    getInfoPopupActionsConfig,
  } = useManageOPInformationDocuments({ C, images, scale });

  const headerProps: ImageHandlingHeaderProps = {
    withoutAttachButton: true,
    withoutFavoriteButton: true,
    C,
    onSuccessImageUpload,
    images,
    getInfoPopupActionsConfig,
    selectedIndex,
    cropImage,
    rotateImage,
    deleteImage,
    setSelectedIndex,
    enabledIcons: [
      HeaderIconKey.CROP,
      HeaderIconKey.ROTATE,
      HeaderIconKey.ADD_IMAGE,
      HeaderIconKey.DISPLAY_MODE,
      HeaderIconKey.MORE,
      HeaderIconKey.CLOSE,
    ],
    setMode,
    bg,
    borderColor: withAttachToBlock ? 'transparent' : borderColor,
    scale,
    font,
    shouldHideDownloadButton,
    shouldHideAttachButton,
    shouldShowAddButtonIfSingleImage,
    shouldHideThumbnailButton,
    shouldDisplayModeIcon,
    iconsContainerStyle: { gap: scale(20), paddingTop: scale(1) },
    imageContextForAdding,
    withNotchTop: false,
    withoutDocTypeButton: true,
    shouldHideIconsWithoutImg: true,
    containerStyle: {
      paddingLeft: scale(24),
      paddingRight: scale(21),
    },
    BackButtonComponent: withBackButton ? <BackButton withoutText onPress={onBackButtonPress} /> : null,
    TitleComponent: (
      <Text ellipsizeMode={'middle'} numberOfLines={1} style={[font.Headline, { color: C('Label/Light/Primary'), flex: 1 }]}>
        {title}
      </Text>
    ),
    onClose,
  };

  const containerBg = images.length === 0 ? bg : imageBg;

  return (
    <View
      testID={testID}
      style={[
        { height: '100%' },
        images.length === 0 && {
          justifyContent: 'center',
        },
      ]}
      onLayout={(event) => {
        const { height, width } = event.nativeEvent.layout;
        setContainerMeasurements({ height, width });
      }}
    >
      <ImageHandlingHeader {...headerProps} />

      {noResult && <NoResult {...{ C, scale, font, tabs, imageContextForAdding, onSuccessImageUpload }} />}
      {withAttachToBlock && (
        <View
          onLayout={(event) => {
            const { height } = event.nativeEvent.layout;
            setAttachBlockMeasurements(height);
          }}
          style={{ position: 'absolute', zIndex: 10000000, top: scale(IMAGE_HANDLING_HEADER_HEIGHT), backgroundColor: bg, flex: 1, width: '100%' }}
        >
          <AttachEntitiesListOP
            selectedImageDocument={selectedImage}
            font={font}
            scale={scale}
            imageDocumentLinks={selectedImage?.links}
            style={{ paddingLeft: scale(22), borderBottomColor: borderColor, borderBottomWidth: StyleSheet.hairlineWidth, minHeight: scale(39) }}
          />
        </View>
      )}
      {!noResult && containerMeasurements.width > 0 && (
        <View
          style={{
            marginTop: scale(IMAGE_HANDLING_HEADER_HEIGHT) + attachBlockMeasurements,
            height: '100%',
            flex: 1,
          }}
        >
          <ImagesDocumentsGallery
            {...{
              cropImage,
              rotateImage,
              deleteImage,
              withoutImageHandlingHeader: true,
              withoutAttachItems: true,
              C,
              containerHeight:
                fullHeight -
                (isWeb ? BOTTOM_SHEET_TOP_OFFSET_WEB : BOTTOM_SHEET_TOP_OFFSET / 2) -
                scale(IMAGE_HANDLING_HEADER_HEIGHT) -
                attachBlockMeasurements,
              containerWidth: containerMeasurements.width,
              selectedIndex,
              setSelectedIndex,
              bigSliderBg: containerBg,
              smallSliderBg: containerBg,
              shouldUseColorWrapperForBigSliderBg: false,
              shouldUseColorWrapperForSmallSliderBg: false,
              glassButtonsStyle: {
                docTypeButton: { marginLeft: scale(IMAGE_PADDING_HORIZONTAL) },
                expandViewButton: { marginRight: scale(IMAGE_PADDING_HORIZONTAL) },
              },
              images,
              scale,
              font,
            }}
          />
        </View>
      )}
    </View>
  );
};

export default connect(
  ({ operatorPanel: { tabs } }) => ({
    tabs,
  }),
  {
    incrementTabCount,
  }
)(ColorWrapper(OperatorPanelInformationDocuments));
